import React, { Component } from "react";
import { connect } from "redux-bundler-react";

const PUBLIC_URL = process.env.PUBLIC_URL;

// Mapbox's Terms of Service require this attribution to remain visible on
// every map view that uses their tiles/styles (Aerial, Street, Light Gray,
// and Dark Gray below are all served from api.mapbox.com).
const MAPBOX_ATTRIBUTION =
  '&copy; <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">Mapbox</a> ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> ' +
  '<a href="https://www.mapbox.com/map-feedback/" target="_blank" rel="noopener noreferrer"><strong>Improve this map</strong></a>';

// Free, keyless fallback for whenever no Mapbox token is configured (e.g. a
// local checkout without .env) so the map still renders something instead of
// sending every basemap request to Mapbox unauthenticated.
const OSM_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';

class BasemapLayers extends Component {
  constructor(props) {
    super(props);
  }

  componentWillReceiveProps() {
    // changeBasemap() itself falls back to OSM automatically when no Mapbox
    // token is configured, so this default works with or without one.
    if (!this.basemapLayer) this.changeBasemap("basemapGray");
  }

  onBasemapChange = (e) => {
    this.changeBasemap(e.target.id);
  };

  changeBasemap = (basemap) => {
    let map = this.props.map;
    let accessToken = `${process.env.REACT_APP_MAPBOX_TOKEN}`;
    // process.env.REACT_APP_MAPBOX_TOKEN interpolates to the string
    // "undefined" (not empty) when the var isn't set at all.
    const hasMapboxToken = accessToken !== "" && accessToken !== "undefined";
    if (map) {
      let url = "";
      let attribution = "";
      // No token -> always fall back to plain OSM tiles regardless of which
      // basemap was requested, rather than sending an unauthenticated
      // request to Mapbox for whichever one it was.
      let isMapboxBasemap = hasMapboxToken;

      if (!hasMapboxToken) {
        url = OSM_TILE_URL;
        attribution = OSM_ATTRIBUTION;
      } else {
        switch (basemap) {
          case "basemapStreet":
            url = `https://api.mapbox.com/styles/v1/usace/cm939k4g0007v01qu4q5x0yew/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`;
            attribution = MAPBOX_ATTRIBUTION;
            break;
          // --- CARTO basemaps (disabled; replaced by Mapbox entries below) ---
          // case "basemapGray":
          //   url =
          //     "https://cartodb-basemaps-c.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png";
          //   break;
          // case "basemapDark":
          //   url =
          //     "https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png";
          //   break;
          case "basemapGray":
            url = `https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`;
            attribution = MAPBOX_ATTRIBUTION;
            break;
          case "basemapDark":
            url = `https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`;
            attribution = MAPBOX_ATTRIBUTION;
            break;
          default:
            url = `https://api.mapbox.com/styles/v1/usace/cm939otin006r01qthub96e0f/tiles/256/{z}/{x}/{y}?access_token=${accessToken}`;
            attribution = MAPBOX_ATTRIBUTION;
        }
      }
      if (this.basemapLayer) map.removeLayer(this.basemapLayer);

      // eslint-disable-next-line
      const altPrefix = `<span title="Copyright (c) 2010-2024, Volodymyr Agafonkin\n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Copyright (c) 2010-2011, CloudMade\n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; All rights reserved."<a href="#">Powered by <u>Leaflet</u></a></span>`;
      this.basemapLayer = window.L.tileLayer(url, { attribution: attribution });
      map.attributionControl.setPrefix(altPrefix);
      map.addLayer(this.basemapLayer);
      this.updateMapboxLogo(map, isMapboxBasemap);
    }
  };

  // Mapbox's Terms of Service also require the Mapbox wordmark to be shown
  // (not just linked in the attribution text) whenever Mapbox tiles are on
  // screen. Add/remove a small logo control in the map's bottom-right corner
  // to match — using the same logo asset mapbox-gl-js itself ships.
  updateMapboxLogo = (map, show) => {
    if (show) {
      if (!this.mapboxLogoControl) {
        const MapboxLogoControl = window.L.Control.extend({
          options: { position: "bottomright" },
          onAdd: function () {
            const container = window.L.DomUtil.create(
              "div",
              "mapbox-logo-ctrl"
            );
            // extra lift above Leaflet's default 10px control margin, so the
            // logo sits higher up the corner instead of hugging the very edge
            container.style.marginBottom = "26px";
            container.innerHTML = `<a href="https://www.mapbox.com/" target="_blank" rel="noopener noreferrer" aria-label="Mapbox"><img src="${PUBLIC_URL}/mapbox-logo.svg" width="66" height="17" alt="Mapbox" /></a>`;
            window.L.DomEvent.disableClickPropagation(container);
            return container;
          },
        });
        this.mapboxLogoControl = new MapboxLogoControl();
      }
      if (!this.mapboxLogoAdded) {
        map.addControl(this.mapboxLogoControl);
        this.mapboxLogoAdded = true;
      }
    } else if (this.mapboxLogoAdded) {
      map.removeControl(this.mapboxLogoControl);
      this.mapboxLogoAdded = false;
    }
  };

  render() {
    return (
      <div className="BasemapLayers">
        <div className="legendTitle">Basemaps</div>
        {/* TEST ONLY: keyless OpenStreetMap option. Delete this block (and the
            "basemapOSM" case above) before production. */}
        {/*<div className="basemap">
          <div className="basemapItem">
            <input
              id="basemapOSM"
              onChange={this.onBasemapChange}
              type="radio"
              defaultChecked={true}
              name="optBasemap"
            />
            OpenStreetMap
          </div>
        </div> */}
        <div className="basemap">
          <div className="basemapItem" title="Aerial imagery basemap">
            <input
              id="basemapAerial"
              onChange={this.onBasemapChange}
              type="radio"
              name="optBasemap"
            />
            Aerial
          </div>
        </div>
        <div className="basemap">
          <div className="basemapItem" title="Street map basemap">
            <input
              id="basemapStreet"
              onChange={this.onBasemapChange}
              type="radio"
              name="optBasemap"
            />
            Street
          </div>
        </div>
        <div className="basemap">
          <div className="basemapItem" title="Light gray reference basemap">
            <input
              id="basemapGray"
              onChange={this.onBasemapChange}
              type="radio"
              name="optBasemap"
            />
            Light Gray
          </div>
        </div>
        <div className="basemap">
          <div className="basemapItem" title="Dark gray reference basemap">
            <input
              id="basemapDark"
              onChange={this.onBasemapChange}
              type="radio"
              name="optBasemap"
            />
            Dark Gray
          </div>
        </div>
      </div>
    );
  }
}

export default connect("selectMap", BasemapLayers);
